import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { Star, Heart, ShoppingCart, Minus, Plus, Truck, Shield, RefreshCw, ChevronLeft, ChevronRight, Check, X, CheckCircle2, XCircle, Loader2, ShieldCheck, MessageSquare, Gift, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GiftCardSuccessModal } from '../components/GiftCardSuccessModal';
import { FeatureDetailModal } from '../components/FeatureDetailModal';
import { ProductCard } from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { CollapsibleDescription } from '../components/CollapsibleDescription';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const paymentRef = searchParams.get('ref');
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedDenomination, setSelectedDenomination] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [voucherDetails, setVoucherDetails] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ open: false, url: '', orderId: '', status: 'pending' });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const pollRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const statusAttemptsRef = useRef(0);

  const inWishlist = product ? isInWishlist(product.product_id) : false;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (productId.startsWith('reward_')) {
          const rewardId = productId.replace('reward_', '');
          const rewardFromState = location.state?.reward;

          if (rewardFromState) {
            // Two denomination shapes (values in paise):
            //  - fixed: eligible_fixed_denomination = [v1, v2, ...] -> chips
            //  - range: eligible_fixed_denomination = [] and
            //           eligible_range_denomination = [min, max]     -> amount input
            // Prefer the admin-marked-up display_* values (face + margin) when the
            // backend sends them so the customer sees/pays the selling price.
            const fixedDenoms = rewardFromState.display_fixed_denomination || rewardFromState.eligible_fixed_denomination || [];
            const rangeDenom = rewardFromState.display_range_denomination || rewardFromState.eligible_range_denomination || [];
            const denominationType =
              rewardFromState.denomination_type ||
              (fixedDenoms.length ? 'fixed' : (rangeDenom.length ? 'range' : 'fixed'));

            const startingPaise =
              rewardFromState.display_price ?? fixedDenoms[0] ?? rangeDenom[0] ?? rewardFromState.amount ?? 0;
            const minAmount = (rangeDenom[0] ?? startingPaise) / 100;
            const maxAmount = (rangeDenom[1] ?? rangeDenom[0] ?? rewardFromState.amount ?? startingPaise) / 100;

            setProduct({
              product_id: productId,
              name: rewardFromState.brand?.name || rewardFromState.display_parameters?.name || rewardFromState.name,
              description: rewardFromState.display_parameters?.description || rewardFromState.description,
              brand: 'Gift Card',
              images: [rewardFromState.display_parameters?.image_url || rewardFromState.brand?.logo_url].filter(Boolean),
              price: startingPaise / 100,
              stock: 999,
              denomination_type: denominationType,
              min_amount: minAmount,
              max_amount: maxAmount,
              denominations: denominationType === 'fixed'
                ? fixedDenoms.map((val, idx) => ({
                    id: `${rewardId}_${idx}`,
                    value: val / 100
                  }))
                : [],
              is_gift_card: true
            });
            setSelectedDenomination({
              id: `${rewardId}_0`,
              value: startingPaise / 100
            });
            setLoading(false);
            return;
          }
        }

        // Parallelize Product and Review fetching
        const [productRes, reviewsRes] = await Promise.all([
          fetch(`${API}/products/${productId}`),
          fetch(`${API}/reviews/${productId}`)
        ]);

        if (productRes.ok) {
          const productData = await productRes.json();
          setProduct(productData);
          
          // Related products depend on category_id
          const relatedRes = await fetch(`${API}/products?category=${productData.category_id}&limit=4`);
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            setRelatedProducts((relatedData.products || []).filter(p => p.product_id !== productId));
          }
        }

        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    setQuantity(1);
    setSelectedImage(0);
    setQuantity(1);
    setSelectedImage(0);
  }, [productId, location.state]);

  useEffect(() => {
    if (paymentRef && product?.is_gift_card) {
      checkPaymentStatus(paymentRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRef, product]);

  const checkPaymentStatus = async (ref) => {
    try {
      setIsProcessingOrder(true);
      const response = await fetch(`${API}/payment/status/${ref}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.status === 'success') {
        const order_id = data.data?.transactionId || data.transaction_id;
        if (order_id) {
          fetchVoucherDetails(order_id);
        } else {
          toast.success('Payment successful! Processing your gift card...');
          // Optional: Poll again or show info
        }
      } else if (data.status === 'failed') {
        toast.error('Payment failed. Please try again or contact support.');
        setIsProcessingOrder(false);
      } else {
        // Still pending — retry, but cap attempts and track the timeout so it can
        // be cleared on unmount (otherwise it polls forever + updates unmounted state).
        statusAttemptsRef.current += 1;
        if (statusAttemptsRef.current >= 40) { // ~2 min
          toast.info('Still confirming your payment. Check "My Orders" for the latest status.');
          setIsProcessingOrder(false);
          return;
        }
        statusTimeoutRef.current = setTimeout(() => checkPaymentStatus(ref), 3000);
      }
    } catch (err) {
      console.error('Status check error:', err);
      setIsProcessingOrder(false);
    }
  };

  useEffect(() => {
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const startPollingPaymentStatus = (orderId) => {
    stopPolling();
    const token = localStorage.getItem('token');
    const check = async () => {
      try {
        const res = await fetch(`${API}/giftcards/razorpay/orders/${orderId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });
        if (!res.ok) return;
        const data = await res.json();
        const pStatus = (data.status || '').toLowerCase(); // Note: NeoPayOut status mapping or razorpay

        if (pStatus === 'paid' || pStatus === 'success' || pStatus === 'completed') {
          stopPolling();
          setPaymentModal(prev => ({ ...prev, status: 'success' }));

          setTimeout(() => {
            setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
            fetchVoucherDetails(orderId);
          }, 2500);
        } else if (pStatus === 'failed' || pStatus === 'cancelled') {
          stopPolling();
          setPaymentModal(prev => ({ ...prev, status: 'failed' }));
        }
      } catch { }
    };
    pollRef.current = setInterval(check, 5000);
  };

  const manualCheckStatus = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/giftcards/razorpay/orders/${paymentModal.orderId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const pStatus = (data.status || '').toLowerCase();

      if (pStatus === 'paid' || pStatus === 'success' || pStatus === 'completed') {
        stopPolling();
        setPaymentModal(prev => ({ ...prev, status: 'success' }));
        setTimeout(() => {
          setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
          fetchVoucherDetails(paymentModal.orderId);
        }, 2500);
      } else if (pStatus === 'failed' || pStatus === 'cancelled') {
        stopPolling();
        setPaymentModal(prev => ({ ...prev, status: 'failed' }));
      } else {
        toast.info('Payment not confirmed yet. Please complete payment and try again.');
        // Optionally manually trigger the status check endpoint also:
        checkPaymentStatus(paymentModal.orderId);
      }
    } catch {
      toast.error('Could not check payment status. Please wait a moment.');
    } finally {
      setCheckingStatus(false);
    }
  };


  const fetchVoucherDetails = async (orderId) => {
    try {
      const response = await fetch(`${API}/giftcards/razorpay/orders/${orderId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch voucher details');

      const orderData = await response.json();
      const voucher = orderData.order?.order_items?.[0]?.vouchers?.[0] ||
        orderData.vouchers?.[0];

      if (voucher) {
        setVoucherDetails(voucher);
        setShowSuccessModal(true);
      } else {
        toast.error('Gift card issued but vouchers not ready. Please check "My Orders".');
      }
    } catch (err) {
      console.error('Voucher fetch error:', err);
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      await addToCart(product.product_id, quantity);
      toast.success('Added to cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBuyNow = async () => {
    if (!user) {
      // Store pending checkout in localStorage
      localStorage.setItem('pendingCheckout', JSON.stringify({
        productId: product.product_id,
        quantity: quantity,
        isGiftCard: !!product.is_gift_card,
        denomination: selectedDenomination?.value || product.price
      }));
      toast.info('Please login to continue checkout');
      navigate('/login?redirect=/checkout');
      return;
    }

    if (product.is_gift_card) {
      // Range cards take a variable amount — enforce the min/max before we pay.
      if (product.denomination_type === 'range') {
        const amt = Number(selectedDenomination?.value ?? product.price);
        if (!amt || Number.isNaN(amt) || amt < product.min_amount || amt > product.max_amount) {
          toast.error(`Amount must be between ${formatPrice(product.min_amount)} and ${formatPrice(product.max_amount)}`);
          return;
        }
      }

      try {
        setIsProcessingOrder(true);
        const rewardId = product.product_id.replace('reward_', '');
        const amount = selectedDenomination?.value || product.price;

        const token = localStorage.getItem('token');
        const buyResponse = await fetch(`${API}/giftcards/purchase`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          credentials: 'include',
          body: JSON.stringify({
            reward_id: rewardId,
            denomination: Math.round(amount * 100),
            quantity: quantity,
            amount: amount * quantity
          })
        });

        if (!buyResponse.ok) {
          const errorData = await buyResponse.json();
          throw new Error(errorData.detail || 'Failed to initiate purchase');
        }

        const buyData = await buyResponse.json();

        if (buyData.payment_url) {
          setPaymentModal({ open: true, url: buyData.payment_url, orderId: buyData.transaction_id || buyData.order_id, status: 'pending' });
          startPollingPaymentStatus(buyData.transaction_id || buyData.order_id);
        } else if (buyData.status === 'success' && buyData.data?.paymentUrl) {
          const orderId = buyData.data?.transactionId || buyData.data?.orderId;
          setPaymentModal({ open: true, url: buyData.data.paymentUrl, orderId: orderId, status: 'pending' });
          startPollingPaymentStatus(orderId);
        } else {
          throw new Error(buyData.message || 'Payment URL not found');
        }
      } catch (err) {
        console.error('Purchase error:', err);
        toast.error(err.message);
        setIsProcessingOrder(false);
      }
      return;
    }

    try {
      await addToCart(product.product_id, quantity);
      navigate('/cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleWishlist = async () => {
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(product.product_id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.product_id);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-surface rounded-3xl animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-surface rounded-xl animate-pulse w-3/4" />
              <div className="h-6 bg-surface rounded-xl animate-pulse w-1/2" />
              <div className="h-12 bg-surface rounded-xl animate-pulse w-1/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-secondary mb-4">Product Not Found</h1>
          <Button onClick={() => navigate('/')} className="rounded-full">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      <SEO 
        title={product.meta_title || product.name} 
        description={product.meta_description || product.short_description?.replace(/<[^>]*>/g, '') || product.description?.replace(/<[^>]*>/g, '').substring(0, 160)} 
        keywords={product.meta_keywords}
        image={getImageUrl(product.images?.[0])}
        url={`https://www.nso-india.com/product/${product.product_id}`}
      />
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4 min-w-0 overflow-hidden" data-testid="product-gallery">
            {/* Main Image */}
            <div className="relative aspect-square bg-white rounded-3xl border border-gray-100 flex items-center justify-center overflow-hidden">
              <img
                src={getImageUrl(product.images?.[selectedImage])}
                alt={product.name}
                onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                className="max-w-full max-h-full object-contain"
              />

              {product.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setSelectedImage((prev) => (prev + 1) % product.images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/80 backdrop-blur rounded-full hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 w-full max-w-full min-w-0">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 border-2 transition-all ${selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                  >
                    <img src={getImageUrl(img)} alt="" onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }} className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6 min-w-0" data-testid="product-info">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body">
              <button onClick={() => navigate(-1)} className="hover:text-primary">
                <ChevronLeft className="w-4 h-4 inline" /> Back
              </button>
            </div>

            {/* Brand */}
            <p className="text-sm uppercase tracking-widest text-primary font-body font-semibold">
              {product.brand}
            </p>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-secondary">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${i < Math.round(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length || 0)
                      ? 'fill-warning text-warning'
                      : 'text-gray-200'
                      }`}
                  />
                ))}
              </div>
              {reviews.length > 0 ? (
                <>
                  <span className="font-body font-semibold text-secondary">
                    {(reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1)}
                  </span>
                  <span className="text-muted-foreground font-body">
                    ({reviews.length} reviews)
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground font-body text-sm italic">
                  No reviews yet. Be the first to review!
                </span>
              )}
            </div>

            {/* Price */}
            <div className="flex flex-col gap-3">
              <div className="flex items-end gap-4">
                <span className="font-body font-bold text-4xl text-secondary">
                  {formatPrice(product.discount_type === 'b1g1' && parseFloat(product.price) === 0 ? product.original_price : product.price)}
                </span>
                {product.original_price > product.price && product.discount_type !== 'b1g1' && (
                  <>
                    <span className="text-xl text-muted-foreground line-through font-body">
                      {formatPrice(product.original_price)}
                    </span>
                    <span className="bg-destructive/10 text-destructive px-3 py-1 rounded-full text-sm font-medium font-body">
                      {discount}% OFF
                    </span>
                  </>
                )}
              </div>

              {/* B1G1 Special Offer Badge */}
              {product.discount_type === 'b1g1' && (
                <div className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white px-4 py-2 rounded-2xl w-fit shadow-md shadow-indigo-100 animate-in slide-in-from-left-2 duration-500">
                  <Gift className="w-4 h-4 fill-white/20" />
                  <span className="text-xs font-bold uppercase tracking-wider">Buy 1 Get 1 FREE</span>
                </div>
              )}
            </div>

            {/* Custom amount input for range (variable-amount) gift cards */}
            {product.is_gift_card && product.denomination_type === 'range' && (
              <div className="space-y-2">
                <span className="text-sm font-body font-medium text-secondary">
                  Enter Amount ({formatPrice(product.min_amount)} – {formatPrice(product.max_amount)}):
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-body font-semibold text-secondary">₹</span>
                  <input
                    type="number"
                    min={product.min_amount}
                    max={product.max_amount}
                    value={selectedDenomination?.value ?? product.min_amount}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      setSelectedDenomination({ id: `${product.product_id}_custom`, value: val });
                      if (typeof val === 'number') {
                        setProduct(prev => ({ ...prev, price: val }));
                      }
                    }}
                    className="w-40 px-4 py-2 rounded-xl border-2 border-gray-100 focus:border-primary outline-none font-body text-sm"
                    data-testid="gift-card-amount-input"
                  />
                </div>
                {selectedDenomination &&
                  typeof selectedDenomination.value === 'number' &&
                  (selectedDenomination.value < product.min_amount || selectedDenomination.value > product.max_amount) && (
                    <p className="text-xs text-destructive font-body">
                      Amount must be between {formatPrice(product.min_amount)} and {formatPrice(product.max_amount)}.
                    </p>
                  )}
              </div>
            )}

            {/* Denomination selection for Gift Cards */}
            {product.is_gift_card && product.denominations?.length > 0 && (
              <div className="space-y-3">
                <span className="text-sm font-body font-medium text-secondary">Select Value:</span>
                <div className="flex flex-wrap gap-2">
                  {product.denominations.map((denom) => (
                    <button
                      key={denom.id}
                      onClick={() => {
                        setSelectedDenomination(denom);
                        setProduct(prev => ({ ...prev, price: denom.value }));
                      }}
                      className={`px-4 py-2 rounded-xl border-2 transition-all font-body text-sm ${selectedDenomination?.id === denom.id
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-gray-100 hover:border-gray-200 text-muted-foreground'
                        }`}
                    >
                      {formatPrice(denom.value)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-success' : 'bg-destructive'}`} />
              <span className="text-sm font-body">
                {product.is_gift_card ? 'In Stock (Instant Delivery)' : (product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock')}
              </span>
            </div>

            {/* Short Description / Highlights */}
            {product.short_description && (
              <div 
                className="prose-custom text-sm bg-surface/40 p-1 rounded-3xl border border-gray-100/50"
                dangerouslySetInnerHTML={{ 
                  __html: (product.short_description || '').replace(/(&nbsp;)+/g, ' ').replace(/\s\s+/g, ' ')
                }}
              />
            )}

            {/* Quantity & Actions */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              {/* Quantity */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-body font-medium text-secondary">Quantity:</span>
                <div className="flex items-center border border-gray-200 rounded-full">
                  <button
                    onClick={() => {
                      if (quantity > 1) {
                        setQuantity(quantity - 1);
                      } else {
                        toast.info('Minimum 1 unit required');
                      }
                    }}
                    className="p-2 hover:bg-surface transition-colors rounded-l-full"
                    data-testid="decrease-qty"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-body font-medium" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => {
                      if (quantity < product.stock) {
                        setQuantity(quantity + 1);
                      } else {
                        toast.error(`Only ${product.stock} units available in stock`);
                      }
                    }}
                    className="p-2 hover:bg-surface transition-colors rounded-r-full"
                    data-testid="increase-qty"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                {!product.is_gift_card && (
                  <Button
                    onClick={handleAddToCart}
                    disabled={product.stock === 0}
                    className="w-full sm:flex-1 bg-secondary hover:bg-secondary/90 text-white rounded-full py-6 font-body btn-press"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                )}
                <Button
                  onClick={handleBuyNow}
                  disabled={product.stock === 0 || isProcessingOrder}
                  className="w-full sm:flex-1 bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press"
                  data-testid="buy-now-btn"
                >
                  {isProcessingOrder ? 'Processing...' : 'Buy Now'}
                </Button>
                {!product.is_gift_card && (
                  <Button
                    onClick={handleWishlist}
                    variant="outline"
                    className={`p-6 rounded-full transition-all duration-300 ${inWishlist ? 'bg-red-50 border-red-200' : 'hover:border-red-200 hover:bg-red-50/50'}`}
                    data-testid="wishlist-btn"
                  >
                    <Heart className={`w-5 h-5 transition-all duration-300 ${inWishlist ? 'fill-red-500 text-red-500 scale-110' : 'text-muted-foreground'}`} />
                  </Button>
                )}
              </div>
            </div>

            {/* Features Icons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-gray-100">
              {[
                { 
                  icon: Truck, 
                  label: 'Free Delivery', 
                  detail: 'Enjoy free standard delivery on all orders above ₹499. No coupon code required!' 
                },
                { 
                  icon: Shield, 
                  label: 'Warranty', 
                  detail: 'This product comes with a 1-year manufacturer warranty covering manufacturing defects.' 
                },
                { 
                  icon: RefreshCw, 
                  label: 'Easy Returns', 
                  detail: 'Not satisfied? Return your product within 7 days of delivery for a full refund or exchange.' 
                }
              ].map((feature, idx) => (
                <button 
                  key={idx} 
                  onClick={() => setSelectedFeature(feature)}
                  className="text-center p-4 bg-surface hover:bg-surface/80 rounded-2xl transition-all group active:scale-95 flex flex-col items-center"
                >
                  <feature.icon className="w-6 h-6 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-body text-muted-foreground font-medium">{feature.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-16 space-y-8">
          {/* Description */}
          <div className="bg-white rounded-3xl p-2 sm:p-4 lg:p-6 overflow-hidden">
            <h2 className="font-heading text-2xl md:text-3xl text-secondary mb-8 pb-4 border-b border-gray-200/50">Full Product Experience</h2>
            <CollapsibleDescription html={product.description} />
          </div>

          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="bg-surface rounded-3xl p-5 sm:p-8">
              <h2 className="font-heading text-2xl text-secondary mb-4">Specifications</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-3 border-b border-gray-200">
                    <span className="font-body text-muted-foreground">{key}</span>
                    <span className="font-body font-medium text-secondary">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="bg-surface rounded-3xl p-5 sm:p-8" id="reviews">
            <h2 className="font-heading text-2xl text-secondary mb-6">
              Customer Reviews ({reviews.length})
            </h2>
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.review_id || review.id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-body font-bold text-secondary text-sm block">{review.user_name || 'Anonymous'}</span>
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-black">Verified Purchase</span>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${i < review.rating ? 'fill-warning text-warning' : 'text-gray-200'
                              }`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-muted-foreground font-body text-sm leading-relaxed">{review.comment}</p>
                    )}
                    <div className="mt-4 pt-4 border-t border-gray-50 flex items-center gap-4 opacity-50">
                      <span className="text-[10px] font-body">Was this helpful?</span>
                      <button className="text-[10px] font-bold hover:text-primary transition-colors">HELPFUL</button>
                      <button className="text-[10px] font-bold hover:text-primary transition-colors">REPORT</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
                <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                <p className="text-muted-foreground font-body">Be the first to review this product!</p>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-3xl text-secondary">Related Products</h2>
              <Link
                to={`/category/${product?.category_id?.replace('cat_', '')}`}
                className="flex items-center gap-2 text-primary font-body font-medium hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
      <GiftCardSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        voucher={voucherDetails || { code: 'N/A', pin: 'N/A' }}
      />

      <FeatureDetailModal 
        isOpen={!!selectedFeature}
        onClose={() => setSelectedFeature(null)}
        feature={selectedFeature}
      />

      {/* ── PAYMENT MODAL ─────────────────────────────────────── */}
      {paymentModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
          {/* Blurred backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

          {/* Modal card */}
          <div
            className="relative w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: '92vh' }}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-900 to-blue-950 text-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">Secure Checkout</p>
                  <p className="text-white/40 text-[11px] mt-0.5">Order #{paymentModal.orderId}</p>
                </div>
              </div>
              {/* Only allow close when pending (not success/failed auto-close) */}
              {paymentModal.status === 'pending' && (
                <button
                  onClick={() => {
                    stopPolling();
                    setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
                    setIsProcessingOrder(false); // Reset button state
                    toast('Payment window closed. Your transaction was cancelled.', { icon: 'ℹ️' });
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* ── SUCCESS SCREEN ── */}
            {paymentModal.status === 'success' && (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center flex-1">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center ring-[10px] ring-emerald-100 mb-6">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                  </div>
                  {/* Pulse ring animation */}
                  <span className="absolute inset-0 rounded-full animate-ping bg-emerald-200 opacity-30" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mt-2 mb-2">Payment Successful! 🎉</h2>
                <p className="text-gray-400 text-sm mb-8 max-w-xs leading-relaxed">
                  Your gift card payment is confirmed! Processing your voucher...
                </p>
                <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 px-5 py-2.5 rounded-full">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating Gift Card...
                </div>
              </div>
            )}

            {/* ── FAILED SCREEN ── */}
            {paymentModal.status === 'failed' && (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center flex-1">
                <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center ring-[10px] ring-red-100 mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h2>
                <p className="text-gray-400 text-sm mb-8 max-w-xs leading-relaxed">
                  Your payment could not be processed. You can retry using the same or a different method.
                </p>
                <div className="flex gap-3 flex-wrap justify-center">
                  <button
                    onClick={() => {
                      setPaymentModal(prev => ({ ...prev, status: 'pending' }));
                      startPollingPaymentStatus(paymentModal.orderId);
                    }}
                    className="px-8 py-3 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm active:scale-95"
                  >
                    Retry Payment
                  </button>
                  <button
                    onClick={() => {
                      stopPolling();
                      setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
                      setIsProcessingOrder(false); // Reset button state
                    }}
                    className="px-8 py-3 border-2 border-gray-200 text-gray-500 rounded-full text-sm font-semibold hover:bg-gray-50 transition-all active:scale-95"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── PAYMENT IFRAME (pending state) ── */}
            {paymentModal.status === 'pending' && (
              <>
                {/* Scrollable iframe — clean, no status bar inside */}
                <div className="overflow-y-auto flex-1" style={{ minHeight: '420px' }}>
                  <iframe
                    src={paymentModal.url}
                    title="Payment Gateway"
                    className="w-full border-0"
                    style={{ minHeight: '560px', display: 'block' }}
                    allow="payment"
                  />
                </div>

                {/* ── Action footer — OUTSIDE the iframe ── */}
                <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <p className="text-center text-[11px] text-gray-400 mb-3">
                    After completing payment on the screen above, tap the button below.
                  </p>
                  <button
                    onClick={manualCheckStatus}
                    disabled={checkingStatus}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-2xl py-3.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                  >
                    {checkingStatus ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Checking payment status...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        I've completed payment
                      </>
                    )}
                  </button>
                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                      <ShieldCheck className="w-3 h-3 text-emerald-500" /> 256-bit SSL
                    </span>
                    <span className="text-gray-200 text-xs">•</span>
                    <span className="text-[10px] text-gray-400">PCI DSS Compliant</span>
                    <span className="text-gray-200 text-xs">•</span>
                    <span className="text-[10px] text-gray-400">Powered by NeoPayOut</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
